import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useCreateLandscapeSchedule } from "@/resources/landscape-schedule";
import { useScheduleForm } from "@/hooks/useScheduleForm";
import { ScheduleFormFields } from "@/components/shared/ScheduleFormFields";

interface ScheduleLandscapeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  landscapeName: string;
  landscapeId: number;
}

export function ScheduleLandscapeModal({
  open,
  onOpenChange,
  landscapeName,
  landscapeId,
}: ScheduleLandscapeModalProps) {
  const [scheduleType, setScheduleType] = useState<"one-time" | "recurring">("recurring");

  const form = useScheduleForm();

  const { mutate: createSchedule, isPending } = useCreateLandscapeSchedule();

  const handleSave = () => {
    if (!form.startDate) {
      toast.error("Start date required");
      return;
    }
    if (form.endDate && form.endDate < form.startDate) {
      toast.error("End date must be on or after start date");
      return;
    }
    const isRecurring = scheduleType === "recurring";
    createSchedule(
      {
        landscapeId,
        payload: {
          scheduleType: scheduleType === "one-time" ? "one_time" : "recurring",
          frequency: isRecurring
            ? (form.frequency as "daily" | "weekly" | "biweekly" | "monthly")
            : undefined,
          dayOfWeek:
            (form.frequency === "weekly" || form.frequency === "biweekly") && isRecurring
              ? form.dayOfWeek
              : undefined,
          dayOfMonth:
            form.frequency === "monthly" && isRecurring
              ? Number(form.dayOfMonth)
              : undefined,
          timeOfDay: form.timeOfDay,
          startDate: format(form.startDate, "yyyy-MM-dd"),
          endDate: form.endDate ? format(form.endDate, "yyyy-MM-dd") : undefined,
          timezone: form.timezone,
        },
      },
      {
        onSuccess: () => {
          toast.success("Schedule saved");
          onOpenChange(false);
        },
        onError: () => {
          toast.error("Failed to save schedule");
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Schedule Landscape Refresh</DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Configure a schedule for &ldquo;{landscapeName}&rdquo;
          </p>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Schedule Type</Label>
            <RadioGroup
              value={scheduleType}
              onValueChange={(v) => setScheduleType(v as "one-time" | "recurring")}
              className="flex gap-4"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="one-time" id="one-time" />
                <Label htmlFor="one-time" className="font-normal cursor-pointer">One-time</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="recurring" id="recurring" />
                <Label htmlFor="recurring" className="font-normal cursor-pointer">Recurring</Label>
              </div>
            </RadioGroup>
          </div>

          <ScheduleFormFields
            values={{
              frequency: form.frequency,
              dayOfWeek: form.dayOfWeek,
              dayOfMonth: form.dayOfMonth,
              startDate: form.startDate,
              timeOfDay: form.timeOfDay,
              timezone: form.timezone,
              endDate: form.endDate,
            }}
            scheduleType={scheduleType === "one-time" ? "one_time" : "recurring"}
            showFrequency
            timezoneOptions={form.timezoneOptions}
            availableHours={form.availableHours}
            isDateDisabled={form.isDateDisabled}
            onFrequencyChange={form.setFrequency}
            onDayOfWeekChange={form.setDayOfWeek}
            onDayOfMonthChange={form.setDayOfMonth}
            onStartDateChange={form.handleStartDateChange}
            onTimeOfDayChange={form.setTimeOfDay}
            onTimezoneChange={form.handleTimezoneChange}
            onEndDateChange={form.setEndDate}
          />
        </div>

        <DialogFooter>
          <Button variant="outline-on-white" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Schedule"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
